/**
 * Shared Tailwind class strings for the SiteOperations.io UI library.
 *
 * One source of truth so every module's search box, chip, select, tab, and
 * button look and behave identically. The platform accent is `primary` (blue)
 * — see `.claude/skills/siteoperations-design/SKILL.md` (v2). These replace
 * the per-module `inputCls` / `chipCls` / `segBtnCls` copies that had drifted
 * across the tools.
 */

/**
 * Density note: every control keeps the 44px touch target on mobile
 * (min-h-[44px] … sm:min-h-0) but sits at a compact ~30–34px from `sm` up —
 * toolbar controls in a professional tool read as quiet chrome, not buttons.
 */

/**
 * The bare 44px touch-target pair for interactive elements that are NOT one of
 * the composed controls below (list rows, tab triggers, lane headers,
 * disclosure rows, menu items). The button/chip/segment strings in this file
 * already include it — reach for this only when composing your own element.
 */
export const touchTarget = 'min-h-[44px] sm:min-h-0';

/** Canonical rounded input/select styling — WITHOUT a width (callers add `w-full` or `w-auto`). */
export const fieldCls =
  'px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400';

/** Toggle / quick-filter chip (pill). One active scheme platform-wide. */
export const chipCls = (active: boolean) =>
  `inline-flex min-h-[44px] items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 text-xs font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0 sm:py-1.5 ${
    active
      ? 'border-primary-600 bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
      : 'border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
  }`;

/** Outline button (Add filter, Clear all, icon+label). `active` tints it primary. */
export const outlineBtnCls = (active = false) =>
  `inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0 ${
    active
      ? 'border-primary-600 text-primary-700 dark:text-primary-400'
      : 'border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
  }`;

/** Square icon-only button (refresh, etc.). 44px touch target on mobile. */
export const iconBtnCls =
  'inline-flex h-11 w-11 items-center justify-center rounded-lg border border-gray-300 text-gray-600 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:h-8 sm:w-8';

/** Segmented-control button (lives inside a bordered track — see SegmentedControl). */
export const segBtnCls = (active: boolean) =>
  `inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-1.5 rounded-md px-2.5 text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0 sm:min-w-0 sm:py-1 ${
    active
      ? 'bg-white text-primary-700 shadow-sm dark:bg-gray-700 dark:text-primary-400'
      : 'text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100'
  }`;

/** Solid primary CTA button (the SKILL.md recipe; large CTA variant swaps in `px-4 py-2`). */
export const primaryBtnCls =
  'inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0';

/** Secondary / outline button with a label (the SKILL.md recipe). */
export const secondaryBtnCls =
  'inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0';

/** Destructive button — same shape as primary, red fill (the SKILL.md recipe). */
export const dangerBtnCls =
  'inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0';
